import type { TrackingMode } from '../../types';

interface ContactTrackingStepProps {
  trackingMode: TrackingMode;
  onTrackingChange: (mode: TrackingMode) => void;
  onNext: () => void;
  onBack: () => void;
}

export function ContactTrackingStep({
  trackingMode,
  onTrackingChange,
  onNext,
  onBack,
}: ContactTrackingStepProps) {
  return (
    <div>
      <p className="text-xs font-medium text-ink-tertiary uppercase tracking-wider mb-1" data-ph-no-mask>
        Step 3 of 4
      </p>
      <h1 className="text-2xl font-bold text-ink mb-1" data-ph-no-mask>Contact Tracking</h1>
      <p className="text-sm text-ink-secondary mb-6 leading-relaxed" data-ph-no-mask>
        Choose how Clawer tracks your contacts. You can change this per-contact later.
      </p>

      <div className="space-y-3">
        {/* Opt-out option */}
        <button
          type="button"
          onClick={() => onTrackingChange('opt_out')}
          className={`w-full text-left rounded-xl border-2 p-4 transition-all duration-200 ${
            trackingMode === 'opt_out'
              ? 'border-accent bg-accent-bg'
              : 'border-edge hover:border-ink-tertiary'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
              trackingMode === 'opt_out' ? 'border-accent' : 'border-ink-tertiary'
            }`}>
              {trackingMode === 'opt_out' && (
                <div className="w-2.5 h-2.5 rounded-full bg-accent" />
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-ink" data-ph-no-mask>
                Opt-out
                <span className="ml-2 text-xs font-medium text-accent">Recommended</span>
              </p>
              <p className="text-xs text-ink-secondary mt-1 leading-relaxed" data-ph-no-mask>
                Track all contacts by default. Anyone can ask to be removed. Best for staying on top of all relationships.
              </p>
            </div>
          </div>
        </button>

        {/* Opt-in option */}
        <button
          type="button"
          onClick={() => onTrackingChange('opt_in')}
          className={`w-full text-left rounded-xl border-2 p-4 transition-all duration-200 ${
            trackingMode === 'opt_in'
              ? 'border-accent bg-accent-bg'
              : 'border-edge hover:border-ink-tertiary'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
              trackingMode === 'opt_in' ? 'border-accent' : 'border-ink-tertiary'
            }`}>
              {trackingMode === 'opt_in' && (
                <div className="w-2.5 h-2.5 rounded-full bg-accent" />
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-ink" data-ph-no-mask>Opt-in</p>
              <p className="text-xs text-ink-secondary mt-1 leading-relaxed" data-ph-no-mask>
                Only track contacts you explicitly add. More privacy-conscious. Good if you want tight control.
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={onBack}
          className="px-4 py-2.5 rounded-lg border border-edge text-sm font-medium text-ink hover:bg-canvas transition-colors"
          data-ph-no-mask
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="px-5 py-2.5 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-deep transition-colors"
          data-ph-no-mask
        >
          Next
        </button>
      </div>
    </div>
  );
}
